import { memo, ReactNode } from "react";
import { Box, Stack } from "@mui/material";
import { useTranslation } from "next-i18next";
import { Typography } from "src/components-mui/base/typography/typography";
import { ListLockedRows } from "src/components-mui/modules/underlying-lists-module/components/list-locked-rows";
import { ListRow } from "src/components-mui/modules/underlying-lists-module/components/list-row";
import {
    UnderlyingListDaysCount,
    UnderlyingListMotiveColor,
    UnderlyingListMotive,
    UnderlyingListOverlayPlacement,
} from "src/components-mui/modules/underlying-lists-module/underlying-lists-module.constants";
import { IUnderlyingListItem } from "src/components-mui/modules/underlying-lists-module/underlying-lists-module.types";

const UNLOCKED_ROWS_COUNT = 1;
const HEADER_LABEL_SX = { fontSize: "inherit", fontWeight: "semibold" } as const;

interface IListTableProps {
    items: IUnderlyingListItem[];
    daysCount: UnderlyingListDaysCount;
    motive: UnderlyingListMotive;
    motiveColor: UnderlyingListMotiveColor;
    expandedIsin: string | null;
    onItemToggle: ({ motive, isin }: { motive: UnderlyingListMotive; isin: string }) => void;
    overlay: ReactNode | null;
    overlayPlacement: UnderlyingListOverlayPlacement;
    ariaLabelledBy: string;
}

const ListTableComponent = ({
    items,
    daysCount,
    motive,
    motiveColor,
    expandedIsin,
    onItemToggle,
    overlay,
    overlayPlacement,
    ariaLabelledBy,
}: IListTableProps) => {
    const { t } = useTranslation(["underlying-overview", "common"]);
    const isLocked = overlay !== null;
    const lastIsin = items.at(-1)?.isin;

    const renderRows = ({ rowItems, firstPosition = 1 }: { rowItems: IUnderlyingListItem[]; firstPosition?: number }) => (
        <Box component="ol" start={firstPosition} aria-labelledby={ariaLabelledBy} sx={{ m: 0, p: 0, listStyle: "none" }}>
            {rowItems.map(item => (
                <ListRow
                    key={item.isin}
                    item={item}
                    daysCount={daysCount}
                    motiveColor={motiveColor}
                    motive={motive}
                    isExpanded={expandedIsin === item.isin}
                    isDisabled={isLocked}
                    isLast={item.isin === lastIsin}
                    onItemToggle={onItemToggle}
                />
            ))}
        </Box>
    );

    return (
        <Box sx={{ mt: { xs: 1, md: 0 } }}>
            <Stack
                direction="row"
                sx={{
                    justifyContent: "space-between",
                    gap: 2,
                    p: 2,
                    borderTop: "2px solid",
                    borderBottom: "2px solid",
                    borderColor: `${motiveColor}.500`,
                    fontSize: { xs: "sm", sm: "md" },
                }}
            >
                <Typography component="span" sx={HEADER_LABEL_SX}>
                    {t("stock", { ns: "common" })}
                </Typography>
                <Typography component="span" sx={{ ...HEADER_LABEL_SX, textAlign: "right" }}>
                    {t("table.performance-in-days", { 0: daysCount })}
                </Typography>
            </Stack>
            {isLocked ? (
                <>
                    {renderRows({ rowItems: items.slice(0, UNLOCKED_ROWS_COUNT) })}
                    <ListLockedRows overlay={overlay} overlayPlacement={overlayPlacement}>
                        {renderRows({ rowItems: items.slice(UNLOCKED_ROWS_COUNT), firstPosition: UNLOCKED_ROWS_COUNT + 1 })}
                    </ListLockedRows>
                </>
            ) : (
                renderRows({ rowItems: items })
            )}
        </Box>
    );
};

export const ListTable = memo(ListTableComponent);
